/**
 * Animated commercial tower with vertical red scanning lines and risk markers.
 */
export default function CommercialPropertyVisual() {
  const markers = [
    { side: "L", top: "12%", label: "Roof Systems" },
    { side: "R", top: "28%", label: "Structural Envelope" },
    { side: "L", top: "44%", label: "Mechanical" },
    { side: "R", top: "60%", label: "Fire / Life-Safety" },
    { side: "L", top: "76%", label: "Tenant Liability" },
    { side: "R", top: "88%", label: "Parking / ADA" },
  ];

  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200 bg-[#fafafa] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.12)]">
      <div className="absolute inset-0 btr-blueprint-overlay opacity-75" />
      <div className="absolute top-3 left-3 text-[10px] font-display tracking-[0.3em] uppercase text-[#a2020b]">
        Commercial · Exposure Map
      </div>
      <div className="absolute top-3 right-3 text-[10px] font-display tracking-[0.2em] uppercase text-[#a2020b] flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#a2020b] btr-red-glow" />
        Scanning
      </div>

      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
        <g fill="none" stroke="#a2020b" strokeWidth="1.4">
          {/* Main tower */}
          <path d="M 320 80 L 320 560 L 520 560 L 520 80 Z" strokeWidth="2" />
          {/* Rooftop */}
          <path d="M 340 80 L 340 40 L 500 40 L 500 80" />
          <rect x="370" y="52" width="30" height="20" opacity="0.7" />
          <rect x="410" y="52" width="30" height="20" opacity="0.7" />
          <rect x="450" y="52" width="30" height="20" opacity="0.7" />
          {/* Floor lines */}
          {Array.from({ length: 10 }).map((_, i) => (
            <path key={i} d={`M 320 ${120 + i * 44} L 520 ${120 + i * 44}`} opacity="0.25" />
          ))}
          {/* Window grid */}
          {Array.from({ length: 10 }).flatMap((_, r) =>
            Array.from({ length: 4 }).map((_, c) => (
              <rect
                key={`${r}-${c}`}
                x={340 + c * 42}
                y={100 + r * 44}
                width="28"
                height="28"
                opacity={(r + c) % 3 === 0 ? "0.7" : "0.35"}
              />
            ))
          )}
          {/* Base / plaza */}
          <path d="M 260 560 L 580 560 L 600 600 L 240 600 Z" strokeWidth="1.6" opacity="0.85" />
          {/* Lobby */}
          <path d="M 390 530 L 390 560 L 450 560 L 450 530" strokeWidth="1.8" />
          {/* Left annex */}
          <path d="M 180 320 L 180 560 L 280 560 L 280 260 L 320 260" opacity="0.6" />
          <path d="M 200 360 L 270 360 M 200 400 L 270 400 M 200 440 L 270 440 M 200 480 L 270 480" opacity="0.3" />
          {/* Right annex */}
          <path d="M 520 260 L 560 260 L 560 560 L 640 560 L 640 320" opacity="0.6" />
          <path d="M 580 360 L 630 360 M 580 400 L 630 400 M 580 440 L 630 440 M 580 480 L 630 480" opacity="0.3" />
          {/* Parking lines */}
          <path d="M 40 600 L 760 600" strokeWidth="2.2" />
          <path d="M 60 585 L 140 585 M 160 585 L 240 585 M 580 585 L 660 585" strokeDasharray="5 4" opacity="0.5" />
        </g>
      </svg>

      {/* Vertical scanning beams */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[#a2020b]"
        style={{ left: "22%", boxShadow: "0 0 14px #a2020b", animation: "btr-vscan-a 4s ease-in-out infinite" }}
      />
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[#a2020b]"
        style={{ left: "78%", boxShadow: "0 0 14px #a2020b", animation: "btr-vscan-b 4.6s ease-in-out infinite" }}
      />

      {/* Risk markers L/R */}
      {markers.map((m, i) => (
        <div
          key={i}
          className={`absolute top-[${m.top}] flex items-center gap-2 ${m.side === "L" ? "left-4" : "right-4 flex-row-reverse"}`}
          style={{ top: m.top }}
        >
          <span className="relative">
            <span className="absolute inset-0 rounded-full bg-[#a2020b]/30 animate-ping" />
            <span className="relative block w-2.5 h-2.5 rounded-full bg-[#a2020b] btr-red-glow" />
          </span>
          <span className="hidden sm:inline text-[10px] font-display font-semibold uppercase tracking-[0.15em] text-[#a2020b] bg-white/80 backdrop-blur-sm px-2 py-0.5 rounded-sm border border-[#a2020b]/20">
            {m.label}
          </span>
        </div>
      ))}

      <style>{`
        @keyframes btr-vscan-a {
          0%, 100% { opacity: 0; transform: translateX(-8px); }
          20% { opacity: 0.9; }
          80% { opacity: 0.9; transform: translateX(40vw); }
          100% { opacity: 0; }
        }
        @keyframes btr-vscan-b {
          0%, 100% { opacity: 0; transform: translateX(8px); }
          20% { opacity: 0.9; }
          80% { opacity: 0.9; transform: translateX(-40vw); }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
